/**
 * Слайдер секции «Обучение».
 * Прокруткой занимается браузер (scroll-snap), скрипт только двигает трек
 * к соседней карточке и держит состояние стрелок в актуальном виде.
 *
 * Всё живёт в IIFE: на главной рядом грузится service.js с такими же именами
 * констант, а два `const` с одним именем в глобальной области роняют второй файл
 * целиком — слайдер тогда не регистрируется и стрелки не появляются нигде.
 */
(() => {
  /** Погрешность в пикселях: субпиксельный scrollLeft не должен считаться «не в начале». */
  const SCROLL_EPSILON = 2;

  const DIRECTION_NEXT = 1;

  class CoursesCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-courses-track]');
      this.nav = this.querySelector('[data-courses-nav]');
      this.prevButton = this.querySelector('[data-courses-prev]');
      this.nextButton = this.querySelector('[data-courses-next]');

      if (!this.track || !this.nav || !this.prevButton || !this.nextButton) {
        console.warn('courses-carousel: разметка неполная, слайдер не инициализирован', this);
        return;
      }

      this.handleScroll = this.handleScroll.bind(this);
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.syncControls = this.syncControls.bind(this);

      this.prevButton.addEventListener('click', this.handlePrev);
      this.nextButton.addEventListener('click', this.handleNext);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(this.syncControls);
      this.resizeObserver.observe(this.track);

      this.syncControls();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    handlePrev() {
      this.scrollToAdjacentCard(-DIRECTION_NEXT);
    }

    handleNext() {
      this.scrollToAdjacentCard(DIRECTION_NEXT);
    }

    handleScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.syncControls();
      });
    }

    /** Левые границы карточек в системе координат прокрутки трека. */
    get cardOffsets() {
      const trackLeft = this.track.getBoundingClientRect().left;
      const scrolled = this.track.scrollLeft;

      return Array.from(this.track.children, (card) =>
        Math.round(card.getBoundingClientRect().left - trackLeft + scrolled)
      );
    }

    get scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    /**
     * Двигает трек к ближайшей карточке в заданном направлении.
     * @param {number} direction 1 - вперёд, -1 - назад
     */
    scrollToAdjacentCard(direction) {
      const offsets = this.cardOffsets;
      const scrolled = this.track.scrollLeft;

      const target =
        direction === DIRECTION_NEXT
          ? offsets.find((offset) => offset > scrolled + SCROLL_EPSILON)
          : offsets.reverse().find((offset) => offset < scrolled - SCROLL_EPSILON);

      if (target === undefined) return;

      this.track.scrollTo({ left: target, behavior: this.scrollBehavior });
    }

    syncControls() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const isScrollable = maxScroll > SCROLL_EPSILON;
      const scrolled = this.track.scrollLeft;

      // Стрелки в макете стоят у заголовка всегда; когда листать нечего, они гаснут,
      // а не исчезают — иначе шапка секции прыгает между брейкпоинтами.
      this.nav.hidden = false;
      this.prevButton.disabled = scrolled <= SCROLL_EPSILON;
      this.nextButton.disabled = scrolled >= maxScroll - SCROLL_EPSILON;

      // Прокручиваемая область должна быть доступна с клавиатуры (WCAG 2.1.1),
      // но лишняя остановка в таб-порядке не нужна, когда прокручивать нечего.
      if (isScrollable) {
        this.track.setAttribute('tabindex', '0');
      } else {
        this.track.removeAttribute('tabindex');
      }
    }
  }

  if (!customElements.get('courses-carousel')) {
    customElements.define('courses-carousel', CoursesCarousel);
  }
})();
